/**
 * View models for the official-weather-warnings card and list, shared by both
 * apps (`cross-app-features`: core owns the decisions and the strings, each app
 * owns the pixels).
 *
 * What crosses the seam: strings, semantic tones and raw epoch times. What does
 * not: colours (each theme maps a `WarningTone`), icon names (each app maps a
 * `WarningType`), and date formatting — the caller passes `formatTime`, so the
 * station's timezone and the 12/24-hour preference stay the app's business and
 * this module keeps no react-intl, store or clock dependency (`intl` and `now`
 * are parameters, as in `utils/Sun`).
 *
 * Imports `./model`, never the utils barrel (`importCycles.test.ts`).
 */
import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';

import type {
  WarningSeverity,
  WarningType,
  WeatherWarning,
  WeatherWarningListResponse,
} from '../../api/types';
import {
  HazardGroup,
  HazardSlice,
  currentWarnings,
  groupByHazard,
  normaliseType,
  severityRank,
  worstSlice,
} from './model';

/** Semantic colour role for a severity; each app's theme maps it to colours. */
export type WarningTone =
  | 'warningMinor'
  | 'warningModerate'
  | 'warningSevere'
  | 'warningExtreme'
  | 'warningUnknown';

const TONES: Record<WarningSeverity, WarningTone> = {
  minor: 'warningMinor',
  moderate: 'warningModerate',
  severe: 'warningSevere',
  extreme: 'warningExtreme',
  unknown: 'warningUnknown',
};

export const severityTone = (severity: WarningSeverity): WarningTone =>
  TONES[severity] ?? 'warningUnknown';

/** The hazard's name, one per backend `WarningType`. */
export const HAZARD_MESSAGES: Record<WarningType, MessageDescriptor> = {
  wind: messages.weatherWarnings.hazardWind,
  rain: messages.weatherWarnings.hazardRain,
  thunderstorm: messages.weatherWarnings.hazardThunderstorm,
  flood: messages.weatherWarnings.hazardFlood,
  snow_ice: messages.weatherWarnings.hazardSnowIce,
  avalanche: messages.weatherWarnings.hazardAvalanche,
  coastal_event: messages.weatherWarnings.hazardCoastalEvent,
  fog: messages.weatherWarnings.hazardFog,
  high_temperature: messages.weatherWarnings.hazardHighTemperature,
  low_temperature: messages.weatherWarnings.hazardLowTemperature,
  forest_fire: messages.weatherWarnings.hazardForestFire,
  uv: messages.weatherWarnings.hazardUv,
  air_quality: messages.weatherWarnings.hazardAirQuality,
  other: messages.weatherWarnings.hazardOther,
};

/**
 * The severity as a word, so colour is never the only cue (WCAG 1.4.1).
 * `unknown` has none: the status lines then fall back to "Now" / "From …" /
 * "Until …" rather than printing "Unknown".
 */
export const SEVERITY_MESSAGES: Record<
  WarningSeverity,
  MessageDescriptor | null
> = {
  minor: messages.weatherWarnings.severityMinor,
  moderate: messages.weatherWarnings.severityModerate,
  severe: messages.weatherWarnings.severitySevere,
  extreme: messages.weatherWarnings.severityExtreme,
  unknown: null,
};

/** The subset of `IntlShape` this needs. */
export type WarningsIntl = {
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, string | number>,
  ) => string;
};

export type WarningsViewParams = {
  intl: WarningsIntl;
  /**
   * Formats an epoch-seconds instant for a status line, in the place's
   * timezone and the user's clock preference, e.g. "tomorrow at 12:00 AM".
   * Build it with `warningTimeFormatter` so both apps agree.
   */
  formatTime: (epochSeconds: number) => string;
  /**
   * What the dashboard is about, which names the matched area: "(this
   * location)" for a saved place, "(your station)" for a station. Default
   * `location`.
   */
  place?: 'location' | 'station';
};

const severityWord = (
  severity: WarningSeverity,
  intl: WarningsIntl,
): string | null => {
  const descriptor = SEVERITY_MESSAGES[severity] ?? null;
  return descriptor ? intl.formatMessage(descriptor) : null;
};

/**
 * When the worst level in force now ends: the LATEST end among the active
 * slices at that level (two overlapping severe slices are one severe spell),
 * or null when one of them runs until further notice. Shared by the row and
 * the band so they never quote different times.
 */
const activeUntil = (group: HazardGroup): number | null => {
  const active = group.slices.filter((s) => s.active);
  const rank = severityRank(worstSlice(active).severity);
  let end: number | null = -Infinity;
  for (const s of active) {
    if (severityRank(s.severity) === rank) {
      end = end === null || s.end === null ? null : Math.max(end, s.end);
    }
  }
  return end;
};

/**
 * The status of one hazard, as one or two segments the app joins with " · "
 * (and the spoken label with "then"):
 *
 * - in force now and getting worse: "Severe now", "Extreme from 12:00"
 * - in force now, not getting worse: "Moderate until 23:59" — the end of the
 *   worst slice in force, not of the whole group; "Moderate now" when it has
 *   no end (until further notice)
 * - not started: "Minor from 14:00", plus "Severe from 17:00" if it escalates
 */
const statusSegments = (
  group: HazardGroup,
  {intl, formatTime}: WarningsViewParams,
): string[] => {
  const activeSlices = group.slices.filter((s) => s.active);
  const upcomingSlices = group.slices.filter((s) => !s.active);

  let first: HazardSlice;
  let firstText: (word: string | null) => string;
  if (activeSlices.length > 0) {
    first = worstSlice(activeSlices);
    const rank = severityRank(first.severity);
    const end = activeUntil(group);
    const escalates = upcomingSlices.some(
      (s) => severityRank(s.severity) > rank,
    );
    firstText = (word) => {
      if (escalates || end === null) {
        return word
          ? intl.formatMessage(messages.weatherWarnings.severityNow, {
              severity: word,
            })
          : intl.formatMessage(messages.common.now);
      }
      const time = formatTime(end);
      return word
        ? intl.formatMessage(messages.weatherWarnings.severityUntil, {
            severity: word,
            time,
          })
        : intl.formatMessage(messages.calendar.untilTime, {time});
    };
  } else {
    // Everything is upcoming: start from the earliest slice (the worst of
    // those sharing that start).
    const earliest = Math.min(...group.slices.map((s) => s.start));
    first = worstSlice(group.slices.filter((s) => s.start === earliest));
    firstText = (word) => {
      const time = formatTime(first.start);
      return word
        ? intl.formatMessage(messages.weatherWarnings.severityFrom, {
            severity: word,
            time,
          })
        : intl.formatMessage(messages.weatherWarnings.fromTime, {time});
    };
  }

  const segments = [firstText(severityWord(first.severity, intl))];
  const later = upcomingSlices.filter(
    (s) => severityRank(s.severity) > severityRank(first.severity),
  );
  if (later.length > 0) {
    const peak = worstSlice(later);
    // Ranked above `first`, so never `unknown`: it always has a word.
    segments.push(
      intl.formatMessage(messages.weatherWarnings.severityFrom, {
        severity: severityWord(peak.severity, intl) as string,
        time: formatTime(peak.start),
      }),
    );
  }
  return segments;
};

type GroupBase = {
  key: string;
  hazard: WarningType;
  /** Tone of the worst level the hazard reaches. */
  tone: WarningTone;
  hazardName: string;
  /** The worst level as a word; null when unknown. */
  severity: string | null;
  /** In force now (else it only starts later). */
  active: boolean;
  /** One or two status segments, e.g. ["Severe now", "Extreme from 12:00"]. */
  line: string[];
  areaName: string | null;
};

/** "Prelitoral de Girona (this location)" + "+ 38 other areas". */
export type WarningAreaLine = {
  name: string;
  /** Null when the warning covers this area only. */
  others: string | null;
};

const areaLine = (
  warning: WeatherWarning,
  {intl, place = 'location'}: WarningsViewParams,
): WarningAreaLine | null => {
  const area = warning.area_name?.trim();
  if (!area) {
    return null;
  }
  const count = warning.areas_count ?? 1;
  return {
    name: intl.formatMessage(
      place === 'station'
        ? messages.weatherWarnings.areaYourStation
        : messages.weatherWarnings.areaThisLocation,
      {area},
    ),
    others:
      count > 1
        ? intl.formatMessage(messages.weatherWarnings.otherAreas, {
            count: count - 1,
          })
        : null,
  };
};

const areaText = (line: WarningAreaLine) =>
  line.others ? `${line.name} ${line.others}` : line.name;

const groupBase = (
  group: HazardGroup,
  params: WarningsViewParams,
): GroupBase => {
  const peak = worstSlice(group.slices);
  return {
    key: group.key,
    hazard: group.hazard,
    tone: severityTone(group.severity),
    hazardName: params.intl.formatMessage(HAZARD_MESSAGES[group.hazard]),
    severity: severityWord(group.severity, params.intl),
    active: group.active,
    line: statusSegments(group, params),
    areaName: peak.warning.area_name || null,
  };
};

export type WarningSummaryRow = GroupBase;

/**
 * One hazard read aloud: "Rain: Severe now, then Extreme from 12:00 AM." The
 * card's spoken label strings these together; the list names each group's
 * header button with one.
 */
export const hazardSentence = (
  row: Pick<GroupBase, 'hazardName' | 'line'>,
  intl: WarningsIntl,
): string =>
  row.line.length > 1
    ? intl.formatMessage(messages.weatherWarnings.spokenHazardTwo, {
        hazard: row.hazardName,
        first: row.line[0],
        second: row.line[1],
      })
    : intl.formatMessage(messages.weatherWarnings.spokenHazardOne, {
        hazard: row.hazardName,
        status: row.line[0],
      });

export type WarningSummaryBand = {
  tone: WarningTone;
  severity: string | null;
  hazard: string;
  /** "Until 23:59" when the worst is in force now, else "From 12:00". */
  when: string;
};

export type WarningSummaryViewModel =
  | {kind: 'none'}
  | {
      kind: 'warnings';
      /**
       * The card's lead: the worst level COMING (not merely the worst now),
       * its hazard and when — "Extreme · Rain · From tomorrow at 12:00 AM".
       */
      band: WarningSummaryBand;
      /** One per hazard, worst first; the card shows the first few. */
      rows: WarningSummaryRow[];
      hazardCount: number;
      /** API rows still current (several can make up one hazard). */
      warningCount: number;
      /**
       * Where the lead warning applies here (its worst slice's area), for the
       * card's area line; null when the row names no area.
       */
      area: WarningAreaLine | null;
      /** "Official weather warnings": the webapp banner's region name. */
      title: string;
      /** "All official warnings": the card footer and the banner link. */
      viewAll: string;
      /** The banner's Show / Hide hazards toggle: only from two hazards. */
      toggle: {show: string; hide: string} | null;
      /**
       * The whole card read as one item: count, one sentence per hazard, then
       * the area line. For `accessibilityLabel` on mobile and `aria-label` on
       * the webapp: either replaces what the card's children would say, so
       * the area line must be in it.
       */
      accessibleName: string;
    };

/**
 * The dashboard card. `warnings` may include expired rows (the point response
 * is cached for an hour); `now` is epoch seconds.
 */
export const summaryViewModel = (
  warnings: readonly WeatherWarning[],
  now: number,
  params: WarningsViewParams,
): WarningSummaryViewModel => {
  const groups = groupByHazard(warnings, now);
  if (groups.length === 0) {
    return {kind: 'none'};
  }
  const {intl, formatTime} = params;
  const rows = groups.map((g) => groupBase(g, params));

  // Groups are sorted worst first (`compareHazardGroups`), so the first holds
  // the worst level coming: the lead `warningsSummaryItem` also names.
  const lead = groups[0];
  const peak = worstSlice(lead.slices);
  const until = peak.active ? activeUntil(lead) : null;
  const band: WarningSummaryBand = {
    tone: rows[0].tone,
    severity: rows[0].severity,
    hazard: rows[0].hazardName,
    when: !peak.active
      ? intl.formatMessage(messages.weatherWarnings.fromTime, {
          time: formatTime(peak.start),
        })
      : until === null
        ? intl.formatMessage(messages.common.now)
        : intl.formatMessage(messages.calendar.untilTime, {
            time: formatTime(until),
          }),
  };
  const area = areaLine(peak.warning, params);

  const sentences = rows.map((row) => hazardSentence(row, intl));

  const label = intl.formatMessage(messages.weatherWarnings.spokenLabel, {
    count: groups.length,
    hazards: sentences.join(' '),
  });

  return {
    kind: 'warnings',
    band,
    rows,
    hazardCount: groups.length,
    warningCount: currentWarnings(warnings, now).length,
    area,
    title: intl.formatMessage(messages.weatherWarnings.title),
    viewAll: intl.formatMessage(messages.weatherWarnings.viewAll),
    toggle:
      groups.length > 1
        ? {
            show: intl.formatMessage(messages.weatherWarnings.showHazards),
            hide: intl.formatMessage(messages.weatherWarnings.hideHazards),
          }
        : null,
    accessibleName: area
      ? intl.formatMessage(messages.weatherWarnings.spokenLabelArea, {
          label,
          area: areaText(area),
        })
      : label,
  };
};

/**
 * "+ 2 more: Wind, Fog": the hazards past the first `shown` rows (the card
 * shows the lead and two more; the collapsed banner only the lead), or null
 * when they all fit.
 */
export const moreHazardsLine = (
  rows: readonly Pick<WarningSummaryRow, 'hazardName'>[],
  shown: number,
  intl: WarningsIntl,
): string | null => {
  const rest = rows.slice(shown);
  return rest.length > 0
    ? intl.formatMessage(messages.weatherWarnings.moreHazards, {
        count: rest.length,
        hazards: rest.map((r) => r.hazardName).join(', '),
      })
    : null;
};

export type WarningListSlice = {
  id: number;
  tone: WarningTone;
  severity: string | null;
  active: boolean;
  /** Epoch seconds; the app formats them for its lane and period rows. */
  start: number;
  /** Null: until further notice. */
  end: number | null;
  /** The period row read aloud: "Severe: Now to today at 23:59". */
  accessibleName: string;
};

/** Period rows shown before "Show all N periods". */
export const MAX_PERIOD_ROWS = 4;

export type WarningListGroup = GroupBase & {
  /**
   * The row the group's detail opens: the earliest at its worst level, whose
   * headline and source the group shows.
   */
  warningId: number;
  /** The worst slice's own headline, from the agency. */
  headline: string;
  /** "Source: Meteo Alarm"; null when the row names none. */
  source: string | null;
  slices: WarningListSlice[];
  /** "Show all 6 periods" past `MAX_PERIOD_ROWS`, else null. */
  showAllPeriods: string | null;
};

export type WarningListSection = {
  id: 'active' | 'upcoming';
  title: string;
  groups: WarningListGroup[];
};

export type WarningEmptyState = {title: string; body: string};

export type WarningListViewModel = {
  /** "Official weather warnings": the screen / page title. */
  title: string;
  /** "Official warnings": the webapp breadcrumb. */
  crumb: string;
  /** "Last update: 21:26", from `computed_at`; null when it is missing. */
  lastUpdate: string | null;
  /** "Active now" then "Upcoming"; an empty section is left out. */
  sections: WarningListSection[];
  /** The empty state when no section is left; null otherwise. */
  empty: WarningEmptyState | null;
};

/**
 * A period row read aloud: its level, then "Now to …" while in force, "… to
 * …" before; an open end reads "Now" / "From …". An unknown level has no
 * word, so the range is read alone.
 */
const spokenPeriod = (
  slice: HazardSlice,
  {intl, formatTime}: WarningsViewParams,
): string => {
  const start = slice.active
    ? intl.formatMessage(messages.common.now)
    : formatTime(slice.start);
  let range: string;
  if (slice.end !== null) {
    range = intl.formatMessage(messages.calendar.periodRangeSpoken, {
      start,
      end: formatTime(slice.end),
    });
  } else if (slice.active) {
    range = start;
  } else {
    range = intl.formatMessage(messages.weatherWarnings.fromTime, {
      time: start,
    });
  }
  const word = severityWord(slice.severity, intl);
  return word
    ? intl.formatMessage(messages.weatherWarnings.spokenPeriod, {
        severity: word,
        range,
      })
    : range;
};

/** The warnings list: hazard groups under "Active now" / "Upcoming". */
export const listViewModel = (
  response: WeatherWarningListResponse,
  now: number,
  params: WarningsViewParams,
): WarningListViewModel => {
  const {intl, formatTime} = params;
  const computedAt = response?.computed_at;
  const groups = groupByHazard(response?.warnings ?? [], now).map(
    (group): WarningListGroup => {
      const peak = worstSlice(group.slices);
      const source = peak.warning.source;
      return {
        ...groupBase(group, params),
        warningId: peak.warning.id,
        headline: peak.warning.headline,
        source: source
          ? intl.formatMessage(messages.weatherWarnings.source, {source})
          : null,
        slices: group.slices.map((s) => ({
          id: s.warning.id,
          tone: severityTone(s.severity),
          severity: severityWord(s.severity, intl),
          active: s.active,
          start: s.start,
          end: s.end,
          accessibleName: spokenPeriod(s, params),
        })),
        showAllPeriods:
          group.slices.length > MAX_PERIOD_ROWS
            ? intl.formatMessage(messages.weatherWarnings.showPeriods, {
                count: group.slices.length,
              })
            : null,
      };
    },
  );

  const sections: WarningListSection[] = [];
  const active = groups.filter((g) => g.active);
  const upcoming = groups.filter((g) => !g.active);
  if (active.length > 0) {
    sections.push({
      id: 'active',
      title: intl.formatMessage(messages.weatherWarnings.activeNow),
      groups: active,
    });
  }
  if (upcoming.length > 0) {
    sections.push({
      id: 'upcoming',
      title: intl.formatMessage(messages.weatherWarnings.upcoming),
      groups: upcoming,
    });
  }

  return {
    title: intl.formatMessage(messages.weatherWarnings.title),
    crumb: intl.formatMessage(messages.weatherWarnings.crumb),
    lastUpdate:
      typeof computedAt === 'number' && Number.isFinite(computedAt)
        ? intl.formatMessage(messages.weatherWarnings.lastUpdate, {
            time: formatTime(computedAt),
          })
        : null,
    sections,
    empty:
      sections.length === 0
        ? {
            title: intl.formatMessage(messages.weatherWarnings.emptyTitle),
            body: intl.formatMessage(messages.weatherWarnings.emptyBody),
          }
        : null,
  };
};

export type WarningErrorViewModel = {
  title: string;
  body: string;
  /** The retry button (shared with the forecast maps' retry). */
  retry: string;
};

/** The list / page when the warnings could not be loaded. */
export const errorViewModel = (intl: WarningsIntl): WarningErrorViewModel => ({
  title: intl.formatMessage(messages.weatherWarnings.errorTitle),
  body: intl.formatMessage(messages.weatherWarnings.errorBody),
  retry: intl.formatMessage(messages.forecastMaps.retry),
});

export type WarningDetailParams = WarningsViewParams & {
  /** The reader's locale, e.g. "de" or "pt-BR". */
  locale: string;
  /**
   * The reader's language named in itself ("Deutsch"), for the note when the
   * agency's text is in another language. No note without it: core cannot
   * name languages on every platform the apps run on.
   */
  readerLanguage?: string;
  /**
   * Every row of the response (expired ones may stay in). The warning is read
   * with the other rows of its hazard, as the list reads it: an AEMET episode
   * split at midnight is one spell, so opening its first row must not say
   * "until 23:59" (or "ended") while the episode goes on. Without them, the
   * row is read alone.
   */
  rows?: readonly WeatherWarning[];
};

export type WarningCredit =
  | {
      kind: 'sender';
      /** "Issued by Météo-France". */
      text: string;
      /** "Open on vigilance.meteofrance.fr"; null without a web page. */
      link: {label: string; url: string} | null;
    }
  /** Before backend MR2 (no `sender_name`): "Source: Meteo Alarm". */
  | {kind: 'source'; text: string};

export type WarningDetailViewModel = {
  /** It ended while on screen: the band then reads "Ended". */
  ended: boolean;
  band: {tone: WarningTone; severity: string | null; hazard: string};
  /** The agency's headline, untranslated. */
  headline: string;
  /** The status segments, or the "ended" sentence. */
  line: string[];
  /** "What to do": only when the agency gives an instruction. */
  instruction: {title: string; body: string} | null;
  /** The agency's text, and a note when it is not in the reader's language. */
  message: {title: string; body: string; languageNote: string | null} | null;
  credit: WarningCredit | null;
  /**
   * The heading, and the area line from the warning row alone: what the
   * section shows until (or unless) `warnings/<id>/areas/` answers. The map,
   * its label and the full list come from `areaListViewModel` (`./areas`).
   */
  area: {
    title: string;
    line: WarningAreaLine | null;
  };
};

/** Other areas listed under the own one before "Show all N areas". */
export const MAX_AREA_ROWS = 6;

const primaryLanguage = (tag: string) =>
  tag.trim().toLowerCase().split(/[-_]/)[0];

/**
 * The host of an http(s) URL without "www.", or null for anything else,
 * including a URL with userinfo: in `https://a.fr@b.example/` the link opens
 * b.example, so naming a.fr would mislead.
 */
const siteOf = (url: string): string | null => {
  const match = /^https?:\/\/([^/?#:@\s]+)(?::\d+)?(?:[/?#]|$)/i.exec(
    url.trim(),
  );
  return match ? match[1].toLowerCase().replace(/^www\./, '') : null;
};

/** One warning on its own: the detail screen and the webapp's detail pane. */
export const detailViewModel = (
  warning: WeatherWarning,
  now: number,
  params: WarningDetailParams,
): WarningDetailViewModel => {
  const {intl, formatTime, locale, readerLanguage} = params;
  const hazard = intl.formatMessage(
    HAZARD_MESSAGES[normaliseType(warning.warning_type)],
  );
  const rows = [
    warning,
    ...(params.rows ?? []).filter((r) => r.id !== warning.id),
  ];
  // Its spell over every row, expired ones included: which rows continue it,
  // and when it ends.
  const spell = groupByHazard(rows, Number.NEGATIVE_INFINITY)
    .flatMap((g) => g.slices)
    .find((s) => s.warnings.some((r) => r.id === warning.id));
  const spellIds = new Set((spell?.warnings ?? [warning]).map((r) => r.id));
  // The hazard as the list reads it now, if any row of the spell is current.
  const group = groupByHazard(rows, now).find((g) =>
    g.slices.some((s) => s.warnings.some((r) => spellIds.has(r.id))),
  );
  const ended = !group;
  const spellEnd = () => spell?.end ?? warning.end_time;

  const description = warning.description?.trim();
  const textLanguage = warning.text_language?.trim();
  const sender = warning.sender_name?.trim();
  const site = warning.source_url ? siteOf(warning.source_url) : null;
  const instruction = warning.instruction?.trim();

  let credit: WarningCredit | null = null;
  if (sender) {
    credit = {
      kind: 'sender',
      text: intl.formatMessage(messages.weatherWarnings.issuedBy, {sender}),
      link:
        site && warning.source_url
          ? {
              label: intl.formatMessage(messages.weatherWarnings.openOn, {
                site,
              }),
              url: warning.source_url.trim(),
            }
          : null,
    };
  } else if (warning.source) {
    credit = {
      kind: 'source',
      text: intl.formatMessage(messages.weatherWarnings.source, {
        source: warning.source,
      }),
    };
  }

  return {
    ended,
    band: ended
      ? {
          tone: 'warningUnknown',
          severity: intl.formatMessage(messages.weatherWarnings.ended),
          hazard,
        }
      : {
          tone: severityTone(group.severity),
          severity: severityWord(group.severity, intl),
          hazard,
        },
    headline: warning.headline,
    line: ended
      ? [
          intl.formatMessage(messages.weatherWarnings.endedAt, {
            // `ended` implies a finite end: an open-ended warning never ends.
            time: formatTime(spellEnd() as number),
          }),
        ]
      : statusSegments(group, params),
    instruction: instruction
      ? {
          title: intl.formatMessage(messages.weatherWarnings.whatToDo),
          body: instruction,
        }
      : null,
    message: description
      ? {
          title: intl.formatMessage(messages.weatherWarnings.agencyMessage),
          body: description,
          languageNote:
            textLanguage &&
            readerLanguage &&
            primaryLanguage(textLanguage) !== primaryLanguage(locale)
              ? intl.formatMessage(messages.weatherWarnings.notInLanguage, {
                  language: readerLanguage,
                })
              : null,
        }
      : null,
    credit,
    area: {
      title: intl.formatMessage(messages.weatherWarnings.areaCovered),
      line: areaLine(warning, params),
    },
  };
};
